import React, { Fragment, useEffect, useState } from 'react';

import { connect } from 'react-redux';

import { AppDispatch } from 'redux-types';
import { AppState } from '@redux/types';
import { ApplicationPrices } from '@redux/reducers/applications/types';
import { CurrenciesState } from '@redux/reducers/currencies/types';
import {
  GroupedPlatforms,
  ProfileWithFullName,
  PurchasedSubscription,
} from '@redux/reducers/profile/types';
import { _getCurrencies } from '@redux/reducers/currencies/selectors';
import { _getPrices } from '@redux/reducers/applications/selectors';
import {
  _getProfile,
  _getPurchasedPlatforms,
  _getPurchasedSubscriptions,
} from '@redux/reducers/profile/selectors';
import { _isAuthorized } from '@redux/reducers/auth/selectors';
import { addProductsToBasket } from '@redux/reducers/basket/action';
import {
  changeEmailPreferences,
  changeUserImage,
  changeUserInfo,
  changeUserPassword,
  getPurchasedInfo,
} from '@redux/reducers/profile/action';

import { compose } from 'redux';
import { login, logout, signUp } from '@redux/reducers/auth/action';

import PageTitle from '@components/atoms/PageTitle/PageTitle';
import Layout from '@components/organisms/Layout/Layout';
import AccountTemplate from '@components/templates/AccountTemplate';
import NotAuthorizedTemplate from '@components/templates/NotAuthorizedTemplate';

import { ROUTE } from '@utils/index';
import { isCN } from '@utils/origin';

import { removeCancelRenewalData } from '@utils/localStorage';

import { HaveAccountPopup } from '@hoc/HaveAccountPopup';
import { I18nTranslation } from '@shared/interfaces/i18n';
import {
  InitialProps,
  NextFCWithInitialProps,
  getInitialPropsWithTranslation,
} from '@lib/getInitialProps';
import { NextPage } from 'next';
import { ParsedUrlQuery } from 'querystring';
import Router, { withRouter } from 'next/router';
import { ShowHaveAccountPopup } from '@shared/interfaces/show-have-account-popup';

import { env } from 'next-runtime-env';

import { useSearchParams } from 'next/navigation';
import { withTranslation } from 'i18n';

type AccountProps = {
  profile: ProfileWithFullName;
  dispatch: AppDispatch;
  isAuthorized: boolean;
  query: ParsedUrlQuery;
  subscriptions: PurchasedSubscription[];
  groupedPlatforms: GroupedPlatforms;
  formattedApplications: Record<string, string>;
  prices: ApplicationPrices[];
  t: I18nTranslation;
  showHaveAccountPopup: ShowHaveAccountPopup;
  currencies: CurrenciesState;
};

const Account: NextPage<AccountProps> = props => {
  const [state, setState] = useState({
    stripe: null,
  });

  const handleReactive = product => {
    props.dispatch(addProductsToBasket([product])).then(() => {
      Router.push(ROUTE.CHECKOUT.SELECT);
    });
  };

  const handleComparePlans = () => {
    Router.push('/#features-table');
  };

  const handleChangeAvatar = () => {
    props.dispatch(changeUserImage());
  };

  const handleChangePassword = () => {
    props.dispatch(changeUserPassword());
  };

  const handleChangeInfo = () => {
    isCN
      ? props.dispatch(changeUserInfo())
      : window.open(env('NEXT_PUBLIC_NEOID_ACCOUNT_URL'), '_blank');
  };

  const handleChangeEmailPreferences = () => {
    props.dispatch(changeEmailPreferences());
  };

  const handleGetPurchasedInfo = () => {
    props.dispatch(getPurchasedInfo());
  };

  const handleLogin = () => {
    props.showHaveAccountPopup(() => {
      props.dispatch(login()).then(handleGetPurchasedInfo);
    });
  };

  const handleSignUp = () => {
    props.dispatch(signUp()).then(handleGetPurchasedInfo);
  };

  const handleLogout = () => {
    removeCancelRenewalData();
    props.dispatch(logout());
  };

  const searchParams = useSearchParams();
  const error = searchParams.get('error');

  /**
   * @todo Remove this condition after the issue with the callback error is fixed
   * This is a workaround for NeoID "Register again" button
   */
  if (error && error === 'Callback') {
    handleSignUp();
  }

  useEffect(() => {
    handleGetPurchasedInfo();

    setState({
      stripe: window.Stripe
        ? window.Stripe(env('NEXT_PUBLIC_STRIPE_PUBLIC_KEY'))
        : state.stripe,
    });
  }, []);

  const {
    isAuthorized,
    profile,
    query,
    dispatch,
    subscriptions,
    prices,
    groupedPlatforms,
    formattedApplications,
    currencies,
    t,
  } = props;

  return (
    <Fragment>
      <PageTitle title={t('headTitle')} />
      <Layout>
        <div id="main-content">
          {isAuthorized ? (
            <AccountTemplate
              onLogout={handleLogout}
              profile={profile}
              onChangeAvatar={handleChangeAvatar}
              onChangeInfo={handleChangeInfo}
              onChangePassword={handleChangePassword}
              onChangeEmailPreferences={handleChangeEmailPreferences}
              onReactive={handleReactive}
              onComparePlans={handleComparePlans}
              query={query}
              dispatch={dispatch}
              subscriptions={subscriptions}
              groupedPlatforms={groupedPlatforms}
              formattedApplications={formattedApplications}
              prices={prices}
              currencies={currencies}
            />
          ) : (
            <NotAuthorizedTemplate
              onLogin={handleLogin}
              onSignUp={handleSignUp}
            />
          )}
        </div>
      </Layout>
    </Fragment>
  );
};

const withConnect = connect((state: AppState, props: InitialProps) => {
  const { query } = props.router;
  const profile = _getProfile(state);
  const { groupedPlatforms, formattedApplications } =
    _getPurchasedPlatforms(state);

  return {
    query,
    profile,
    subscriptions: _getPurchasedSubscriptions(state),
    groupedPlatforms,
    formattedApplications,
    isAuthorized: _isAuthorized(state),
    prices: _getPrices(state),
    currencies: _getCurrencies(state),
  };
});

const WrapperAccount = compose<NextFCWithInitialProps<AccountProps>>(
  withRouter,
  withConnect,
  withTranslation('account'),
  HaveAccountPopup
)(Account);

WrapperAccount.getInitialProps = getInitialPropsWithTranslation(['account']);

export default WrapperAccount;
